import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useWindowSize } from 'hooks';
import iconMapper from './iconMapper';

const DevelopmentTechnologies = ({ categories }) => {
  const { windowWidth } = useWindowSize();
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedTechnology, setSelectedTechnology] = useState(0);

  return (
    <Container>
      <Display>
        <Categories>
          {categories.map((category, index) => (
            <Category
              key={category.title}
              onClick={() => {
                setSelectedCategory(index);
                setSelectedTechnology(0);
              }}
              aria-label={`Select ${category.title}`}
              type="button"
              selected={selectedCategory === index}>
              <p>{category.title}</p>
              {windowWidth > 720 && index < categories.length - 1 && <span>/</span>}
            </Category>
          ))}
        </Categories>
        <TechnologyInfo
          key={selectedCategory + selectedTechnology}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.5 }}>
          <div>
            {categories[selectedCategory].technologies[selectedTechnology].icon &&
              (() => {
                const Icon =
                  iconMapper[categories[selectedCategory].technologies[selectedTechnology].icon];
                return <Icon aria-hidden="true" />;
              })()}
          </div>
          {categories[selectedCategory].technologies[selectedTechnology].showTitle && <h3>{categories[selectedCategory].technologies[selectedTechnology].title}</h3>}
          <p
            dangerouslySetInnerHTML={{
              __html: categories[selectedCategory].technologies[selectedTechnology].moreInfo
            }}
          />
        </TechnologyInfo>
      </Display>
      <TechnologyIcons larger={categories[selectedCategory].technologies.length > 6}>
        {categories[selectedCategory].technologies.map((technology, index) => (
          <IconWrapper
            key={`${selectedCategory}-${technology.title}`}
            onClick={() => setSelectedTechnology(index)}
            index={index}
            larger={categories[selectedCategory].technologies.length > 6}
            selected={selectedTechnology === index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: index * 0.1,
              ease: 'easeOut'
            }}>
            {technology.icon &&
              (() => {
                const Icon = iconMapper[technology.icon];
                return <Icon aria-hidden="true" />;
              })()}
            <p>{technology.title}</p>
          </IconWrapper>
        ))}
      </TechnologyIcons>
      
      {/* SEO-friendly hidden content */}
      <SEOContent>
        {categories.map((category, categoryIndex) => (
          <div key={`seo-category-${categoryIndex}`}>
            <h2>{category.title}</h2>
            {category.technologies.map((technology, techIndex) => (
              <div key={`seo-tech-${categoryIndex}-${techIndex}`}>
                <h3>{technology.title}</h3>
                <div
                  dangerouslySetInnerHTML={{
                    __html: technology.moreInfo
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </SEOContent>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  width: 100%;
  margin-top: -2rem;
  @media (min-width: 1200px) {
    display: grid;
    grid-template-columns: 700px 1fr;
    gap: 3rem;
    margin-top: 0;
  }
`;

const Display = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Categories = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media (min-width: 48rem) {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
`;

const Category = styled.button`
  gap: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  p {
    font-size: 1.25rem;
    font-weight: 700;
    color: ${({ selected }) => (selected ? 'var(--ax-gold)' : '#c1beac')};
    letter-spacing: 0.05em;
    text-align: left;
    padding: 0;
  }
  span {
    color: #c1beac;
    font-weight: 700;
  }
  @media (min-width: 48rem) {
    p {
      font-size: 1rem;
      text-decoration: underline;
      text-decoration-position: under;
      text-underline-offset: 0.125em;
    }
  }
`;

const TechnologyIcons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  @media (min-width: 620px) {
    grid-template-columns: repeat(6, 1fr);
  }
  @media (min-width: 1200px) {
    margin-top: 3rem;
    grid-template-columns: repeat(3, 1fr);
  }
`;

const IconWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  max-height: 100px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(1.25);
    filter: grayscale(0);
  }

  svg {
    width: 100%;
    height: 100%;
  }
  p {
    text-align: center;
    font-weight: 700;
  }
  @media (min-width: 1200px) {
    grid-column: ${({ index, larger }) => {
      if (larger) return 'auto';

      const col = (index % 2) + 1; // 1 or 2, never 3
      return `${col} / ${col + 1}`;
    }};
  }
`;

const TechnologyInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: var(--ax-white);
  border: 1px solid var(--ax-gold);
  padding: 2rem 1.5rem 3rem 1.5rem;
  gap: 1.5rem;
  position: relative;
  h3 {
    font-size: 2rem;
    font-weight: 700;
  }
  svg {
    width: auto;
    height: 100%;
    max-height: 112px;
    max-width: 278px;
  }
  @media (min-width: 64rem) {
    padding: 2rem 3rem 4rem 2rem;
  }
`;

const SEOContent = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  div {
    margin-bottom: 1rem;
  }
`;

export default DevelopmentTechnologies;
